import React, { useMemo } from 'react';
import { useLang } from '../../../i18n/LangContext';
import { langToBcp47 } from '../../../i18n/detectLanguage';
import { useT } from '../../../i18n/useT';
import { colors, borders, fonts, ScoreColor } from './tokens';

/**
 * CLDR plural category for a count - picks the right `{day|days}` form.
 * Covers English (one / other) and Ukrainian (one / few / many).
 */
function pluralCategory(n: number): 'one' | 'few' | 'many' {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'one';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
    return 'many';
}

interface RetakeSectionProps {
    /** When the user's most recent assessment was completed. Null = unknown. */
    lastTakenAt: Date | null;
    /** Days between assessments. Defaults to 30. */
    cooldownDays?: number;
    /** Estimated quiz duration shown in the meta block. */
    durationLabel?: string;
    /** Score color drives the ring + accent. */
    score: ScoreColor;
    /** Called when the unlocked CTA is clicked. */
    onRetake: () => void;
}

const RetakeSection: React.FC<RetakeSectionProps> = ({
    lastTakenAt,
    cooldownDays = 30,
    durationLabel,
    score,
    onRetake,
}) => {
    const { lang } = useLang();
    const t = useT();
    const dateFmt = useMemo(
        () => new Intl.DateTimeFormat(langToBcp47(lang), { month: 'short', day: 'numeric', year: 'numeric' }),
        [lang],
    );

    const now = new Date();
    const unlocksAt = lastTakenAt ? new Date(lastTakenAt.getTime() + cooldownDays * 86400000) : null;
    const msLeft = unlocksAt ? unlocksAt.getTime() - now.getTime() : 0;
    const daysLeft = unlocksAt ? Math.max(0, Math.ceil(msLeft / 86400000)) : 0;
    const isUnlocked = !lastTakenAt || daysLeft <= 0;
    const progress = isUnlocked ? 1 : Math.max(0, Math.min(1, (cooldownDays - daysLeft) / cooldownDays));

    const ringSize = 280;
    const stroke = 3;
    const r = (ringSize - stroke) / 2;
    const c = 2 * Math.PI * r;
    const ringColor = isUnlocked ? '#84cc16' : score.hex;

    return (
        <section
            className="dash-edit__retake dash-edit__shell"
            style={{
                padding: '36px 56px 72px',
                display: 'grid',
                gridTemplateColumns: '1.3fr 280px',
                gap: 56,
                alignItems: 'center',
                maxWidth: 1440,
                margin: '0 auto',
            }}
        >
            <div>
                <div
                    style={{
                        fontSize: 11,
                        letterSpacing: '0.22em',
                        color: colors.muted,
                        marginBottom: 14,
                        fontFamily: fonts.mono,
                    }}
                >
                    {t('dashboard.retake.eyebrow')}
                </div>

                <h2
                    style={{
                        fontSize: 44,
                        fontWeight: 300,
                        letterSpacing: '-0.03em',
                        margin: 0,
                        lineHeight: 1.05,
                    }}
                >
                    <span style={{ color: colors.textPrimary }}>{t('dashboard.retake.title_1')}</span>
                    <br />
                    <span style={{ color: colors.muted }}>{t('dashboard.retake.title_2')} </span>
                    <span style={{ color: score.hex }}>{t('dashboard.retake.title_3')}</span>
                </h2>

                <p
                    style={{
                        margin: '20px 0 0',
                        fontSize: 14,
                        color: colors.muted,
                        lineHeight: 1.6,
                        maxWidth: 540,
                    }}
                >
                    {t('dashboard.retake.body', { days: cooldownDays })}
                </p>

                <div
                    style={{
                        height: 1,
                        background: 'rgba(232, 230, 224, 0.08)',
                        margin: '28px 0',
                        maxWidth: 540,
                    }}
                />

                <div style={{ display: 'flex', gap: 56, flexWrap: 'wrap' }}>
                    <Stat
                        label={t('dashboard.retake.stat.last_taken')}
                        value={lastTakenAt ? dateFmt.format(lastTakenAt) : '-'}
                    />
                    <Stat
                        label={t('dashboard.retake.stat.unlocks')}
                        value={unlocksAt ? dateFmt.format(unlocksAt) : t('dashboard.retake.unlocks_now')}
                    />
                    <Stat
                        label={t('dashboard.retake.stat.duration')}
                        value={durationLabel ?? t('dashboard.retake.duration_value')}
                    />
                </div>

                <div style={{ marginTop: 28 }}>
                    {isUnlocked ? (
                        <button
                            type="button"
                            onClick={onRetake}
                            style={{
                                background: '#f4f2ee',
                                color: '#0a0a0a',
                                border: 'none',
                                padding: '12px 22px',
                                borderRadius: 999,
                                fontSize: 14,
                                fontWeight: 500,
                                letterSpacing: '-0.005em',
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                            }}
                        >
                            {t('dashboard.retake.cta')}
                        </button>
                    ) : (
                        <span
                            aria-disabled
                            style={{
                                display: 'inline-block',
                                padding: '10px 18px',
                                borderRadius: 999,
                                border: borders.hairline,
                                color: colors.muted,
                                fontSize: 13,
                                letterSpacing: '-0.005em',
                                background: 'transparent',
                            }}
                        >
                            {t('dashboard.retake.locked', {
                                days: daysLeft,
                                unit: t(`dashboard.retake.days_${pluralCategory(daysLeft)}`),
                            })}
                        </span>
                    )}
                </div>
            </div>

            <div
                className="dash-edit__retake-ring"
                style={{
                    position: 'relative',
                    width: ringSize,
                    height: ringSize,
                    margin: '0 auto',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        inset: -20,
                        background: `radial-gradient(circle at 50% 50%, ${ringColor}1a 0%, ${ringColor}08 30%, transparent 60%)`,
                        filter: 'blur(8px)',
                    }}
                />
                <svg
                    width={ringSize}
                    height={ringSize}
                    style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
                >
                    <circle
                        cx={ringSize / 2}
                        cy={ringSize / 2}
                        r={r}
                        stroke="rgba(255,255,255,0.06)"
                        strokeWidth={stroke}
                        fill="none"
                    />
                    <circle
                        cx={ringSize / 2}
                        cy={ringSize / 2}
                        r={r}
                        stroke={ringColor}
                        strokeWidth={stroke}
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={c}
                        strokeDashoffset={c - progress * c}
                        style={{ filter: `drop-shadow(0 0 6px ${ringColor}88)`, transition: 'stroke-dashoffset .6s ease' }}
                    />
                </svg>
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <div
                        style={{
                            fontSize: 11,
                            letterSpacing: '0.22em',
                            color: colors.muted,
                            marginBottom: 6,
                            fontFamily: fonts.mono,
                        }}
                    >
                        {isUnlocked ? t('dashboard.retake.ring.ready') : t('dashboard.retake.ring.unlocks_in')}
                    </div>
                    <div
                        style={{
                            fontSize: 92,
                            fontWeight: 300,
                            letterSpacing: '-0.05em',
                            lineHeight: 1,
                            color: colors.textPrimary,
                        }}
                    >
                        {isUnlocked ? '✓' : daysLeft}
                    </div>
                    <div
                        style={{
                            fontSize: 11,
                            letterSpacing: '0.22em',
                            color: colors.muted,
                            marginTop: 8,
                            fontFamily: fonts.mono,
                        }}
                    >
                        {isUnlocked ? '' : t(`dashboard.retake.ring.days_${pluralCategory(daysLeft)}`)}
                    </div>
                </div>
            </div>
        </section>
    );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div>
        <div
            style={{
                fontSize: 10,
                letterSpacing: '0.22em',
                color: colors.muted,
                marginBottom: 8,
                fontFamily: fonts.mono,
            }}
        >
            {label}
        </div>
        <div
            style={{
                fontSize: 18,
                color: colors.textPrimary,
                letterSpacing: '-0.01em',
                fontWeight: 400,
            }}
        >
            {value}
        </div>
    </div>
);

export default RetakeSection;
